import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { hasFontVariant } from "@/fonts/catalog";
import { widgetMenuOptionsAtom, widgetMenuOptionsByNameAtom } from "@/interface/AppStore";
import { useHotkeys } from "@/interface/hooks";
import { applyValue, setNewOptions } from "../../lib";
import type { ActiveSubMenuState, SetActiveSubMenu } from "../../types";
import { SubMenu } from "../SubMenu/SubMenu";
import { TextFormatMenuContent } from "./TextFormatMenuContent";

interface TextFormatMenuProps {
    activeSubMenu: ActiveSubMenuState;
    setActiveSubMenu: SetActiveSubMenu;
}

export function TextFormatMenu({ activeSubMenu, setActiveSubMenu }: TextFormatMenuProps) {
    const textBoldOptions = useAtomValue(widgetMenuOptionsByNameAtom("textBold"));
    const textStrikeOptions = useAtomValue(widgetMenuOptionsByNameAtom("textStrike"));
    const textItalicOptions = useAtomValue(widgetMenuOptionsByNameAtom("textItalic"));
    const textUnderlineOptions = useAtomValue(widgetMenuOptionsByNameAtom("textUnderline"));
    const textFontOptions = useAtomValue(widgetMenuOptionsByNameAtom("textFont"));
    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);

    const boldOptionValues = [...new Set(textBoldOptions.map((option) => option.value))];
    const strikeOptionValues = [...new Set(textStrikeOptions.map((option) => option.value))];
    const hasBoldOption = textBoldOptions.length > 0;
    const hasItalicOption = textItalicOptions.length > 0;
    const hasUnderlineOption = textUnderlineOptions.length > 0;
    const isBoldEnabled = boldOptionValues.length === 1 && !!boldOptionValues[0];
    const italicOptionValues = [...new Set(textItalicOptions.map((option) => option.value))];
    const underlineOptionValues = [...new Set(textUnderlineOptions.map((option) => option.value))];
    const isItalicEnabled = italicOptionValues.length === 1 && Boolean(italicOptionValues[0]);
    const isUnderlineEnabled =
        underlineOptionValues.length === 1 && Boolean(underlineOptionValues[0]);
    const isStrikeEnabled = strikeOptionValues.length === 1 && (strikeOptionValues[0] as boolean);

    const selectedFontFamilies = textFontOptions
        .map((option) => option.value)
        .filter((value): value is string => typeof value === "string");
    const canDisplayBoldButton =
        selectedFontFamilies.length === 0 ||
        selectedFontFamilies.every((font) => hasFontVariant(font, "bold"));
    const canDisplayItalicButton =
        selectedFontFamilies.length === 0 ||
        selectedFontFamilies.every((font) => hasFontVariant(font, "italic"));

    const handleTextBoldClick = useCallback(() => {
        const setTextBoldOptions = setNewOptions({ name: "textBold", setWidgetMenuOptions });

        applyValue({
            options: textBoldOptions,
            value: isBoldEnabled ? "" : "bold",
            setNewOptions: setTextBoldOptions,
            handleClose: () => setActiveSubMenu(null),
        });
    }, [textBoldOptions, isBoldEnabled, setWidgetMenuOptions, setActiveSubMenu]);

    const handleTextStrikeClick = useCallback(() => {
        const setTextStrikeOptions = setNewOptions({
            name: "textStrike",
            setWidgetMenuOptions,
        });

        applyValue({
            options: textStrikeOptions,
            value: !isStrikeEnabled,
            setNewOptions: setTextStrikeOptions,
            handleClose: () => setActiveSubMenu(null),
        });
    }, [textStrikeOptions, isStrikeEnabled, setWidgetMenuOptions, setActiveSubMenu]);

    const handleTextItalicClick = useCallback(() => {
        const setTextItalicOptions = setNewOptions({ name: "textItalic", setWidgetMenuOptions });

        applyValue({
            options: textItalicOptions,
            value: !isItalicEnabled,
            setNewOptions: setTextItalicOptions,
            handleClose: () => setActiveSubMenu(null),
        });
    }, [textItalicOptions, isItalicEnabled, setWidgetMenuOptions, setActiveSubMenu]);

    const handleTextUnderlineClick = useCallback(() => {
        const setTextUnderlineOptions = setNewOptions({
            name: "textUnderline",
            setWidgetMenuOptions,
        });

        applyValue({
            options: textUnderlineOptions,
            value: !isUnderlineEnabled,
            setNewOptions: setTextUnderlineOptions,
            handleClose: () => setActiveSubMenu(null),
        });
    }, [textUnderlineOptions, isUnderlineEnabled, setWidgetMenuOptions, setActiveSubMenu]);

    useHotkeys("cmd+b, ctrl+b", handleTextBoldClick);

    return (
        <SubMenu activeSubMenu={activeSubMenu}>
            <TextFormatMenuContent
                onTextBoldButtonClick={handleTextBoldClick}
                onTextStrikeButtonClick={handleTextStrikeClick}
                onTextItalicButtonClick={handleTextItalicClick}
                onTextUnderlineButtonClick={handleTextUnderlineClick}
                isBoldActive={isBoldEnabled}
                isStrikeActive={isStrikeEnabled}
                isItalicActive={isItalicEnabled}
                isUnderlineActive={isUnderlineEnabled}
                showBold={canDisplayBoldButton && hasBoldOption}
                showItalic={canDisplayItalicButton && hasItalicOption}
                showUnderline={hasUnderlineOption}
            />
        </SubMenu>
    );
}
